if (!customElements.get("sticky-atc")) {
  customElements.define("sticky-atc", class StickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.container = this.closest('.m-sticky-addtocart');
      this.scope = this.closest('.m-main-product');
      this.mainProductForm = this.scope?.querySelector('product-form.m-product-form--main');
      this.variantSelect = this.querySelector('[name="id"]');
      if (!this.container || !this.mainProductForm || !this.variantSelect) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.mainProductInfo = this.scope.querySelector('.m-main-product--info');
      this.mainAddToCart = this.mainProductForm.querySelector('.m-add-to-cart');
      this.mainDynamicCheckout = this.mainProductForm.querySelector('.m-product-dynamic-checkout');
      this.hasCustomFields = Boolean(this.mainProductInfo?.querySelector('.m-product-custom-field'));
      try {
        this.variantData = JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
      } catch (error) {
        // Preserve the server-rendered purchase state if product data is unavailable.
        this.abort.abort();
        this.abort = null;
        return;
      }

      this.scope.addEventListener('sd:variant-change', (event) => {
        if (event.detail.picker !== this.scope.querySelector('variant-picker')) return;
        this.setVariant(event.detail.variant);
      }, options);
      this.addEventListener('change', (event) => {
        if (event.target !== this.variantSelect) return;
        const picker = this.scope.querySelector('variant-picker');
        if (picker?.selectVariantById) {
          // A single variant selection drives the main form, sticky form and notices.
          picker.selectVariantById(this.variantSelect.value);
        } else {
          this.setVariant(this.variantData.find((variant) => String(variant.id) === this.variantSelect.value));
        }
      }, options);
      ['matchMobile', 'unmatchMobile'].forEach((event) => {
        document.addEventListener(event, () => this.setStickyAddToCartHeight(), options);
      });

      const picker = this.scope.querySelector('variant-picker');
      let initialId = this.mainProductForm.querySelector('[name="id"]')?.value;
      if (this.dataset.disableSelectedVariantDefault === 'true' && this.variantData.length > 1 &&
          !new URLSearchParams(window.location.search).has('variant')) initialId = '';
      this.setVariant(picker && 'currentVariant' in picker ? picker.currentVariant :
        this.variantData.find((variant) => String(variant.id) === String(initialId)));
      this.initVisibility(options);
      this.initCustomFields(options);
      this.setStickyAddToCartHeight();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      document.documentElement.classList.remove('stick-atc-show');
      document.documentElement.style.removeProperty('--f-sticky-atc-bar-height');
      if (window.MinimogSettings) window.MinimogSettings.stickyAddToCartHeight = 0;
    }

    setVariant(variant) {
      this.currentVariant = variant || null;
      this.variantSelect.value = variant ? String(variant.id) : '';
      this.updateButton(!variant || !variant.available,
        variant ? window.MinimogStrings.soldOut : window.MinimogStrings.unavailable);
      this.scope.querySelector('sd-product-fulfillment')?.updateSticky(this.variantSelect.value);
    }

    updateButton(disable, text) {
      const form = this.querySelector('.sticky-atc-form');
      const button = form?.querySelector('[name="add"]');
      const checkout = form?.querySelector('.m-product-dynamic-checkout');
      const forced = this.mainProductForm.dataset.forceSoldOut === 'true' || button?.dataset.forceSoldOut === 'true';
      const disabled = Boolean(disable || forced);
      if (button) {
        const label = forced ? (button.dataset.soldOutText || window.MinimogStrings.soldOut) :
          (disabled ? (text || window.MinimogStrings.soldOut) : window.MinimogStrings.addToCart);
        button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
        button.classList.toggle('disabled', disabled);
        button.classList.toggle('is-sold-out', forced || Boolean(this.currentVariant && !this.currentVariant.available));
        button.dataset.atcText = label;
        const span = button.querySelector('.m-add-to-cart--text');
        if (span && span.textContent !== label) span.textContent = label;
      }
      if (checkout) {
        checkout.hidden = disabled;
        checkout.inert = disabled;
        checkout.classList.toggle('disabled', disabled);
      }
    }

    initVisibility(options) {
      if (!this.mainAddToCart) {
        this.container.style.setProperty('--m-translate-y', 0);
        return;
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const show = entry.intersectionRatio !== 1;
            this.container.style.setProperty('--m-translate-y', show ? 0 : '100%');
            document.documentElement.classList.toggle('stick-atc-show', show);
          });
        }, { threshold: 1, rootMargin: `${MinimogSettings.headerHeight || 66}px 0px 0px 0px` });
        this.observer.observe(this.mainProductForm);
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.setStickyAddToCartHeight());
        this.resizeObserver.observe(this);
      }
      this.querySelectorAll('.m-sticky-addtocart--title, .m-sticky-addtocart--image').forEach((element) => {
        element.addEventListener('click', () => __scrollToTop(), options);
      });
    }

    initCustomFields(options) {
      if (!this.hasCustomFields) return;
      const addButton = this.querySelector('.m-add-to-cart');
      addButton?.addEventListener('click', (event) => {
        // Submit the main form so every custom line-item property is included.
        event.preventDefault();
        event.stopPropagation();
        const quantity = this.querySelector('[name="quantity"]');
        const mainForm = this.mainProductForm.querySelector('form');
        let mainQuantity = this.mainProductForm.querySelector('[name="quantity"]');
        if (quantity && mainForm) {
          if (!mainQuantity) {
            mainQuantity = document.createElement('input');
            mainQuantity.type = 'hidden';
            mainQuantity.name = 'quantity';
            mainForm.appendChild(mainQuantity);
          }
          mainQuantity.value = quantity.value;
          mainQuantity.dispatchEvent(new Event('change', { bubbles: true }));
        }
        __scrollToTop(this.mainProductForm, () => {
          if (this.mainAddToCart) this.mainAddToCart.click();
          else mainForm?.requestSubmit();
        });
      }, options);
      this.querySelector('.m-product-dynamic-checkout')?.addEventListener('click', (event) => {
        const missing = validateForm(this.mainProductInfo);
        if (missing?.length) {
          event.preventDefault();
          event.stopPropagation();
          __scrollToTop(this.mainProductForm, () => this.mainDynamicCheckout?.click());
        }
      }, { ...options, capture: true });
      this.querySelector('.foxkit-button')?.addEventListener('click', (event) => {
        event.preventDefault();
        __scrollToTop(this.mainProductForm, () => this.mainAddToCart?.click());
      }, options);
    }

    setStickyAddToCartHeight() {
      const height = this.offsetHeight;
      document.documentElement.style.setProperty('--f-sticky-atc-bar-height', `${height}px`);
      window.MinimogSettings.stickyAddToCartHeight = height;
    }
  });
}
